/**
 * The vocabulary the V2 capability surface speaks.
 *
 * Everything here is presentation only. The authoritative module list, role list and
 * immutable set all come from `GET /r/v2/workspaces/{id}/capabilities` at runtime: a
 * module added or an immutable rule changed backend-side must show up without a frontend
 * release, so nothing in this file may be treated as the source of truth.
 */

export const ACCESS_LEVELS = [
  'MANAGE', 'READ', 'ASSIGNED_ONLY', 'READ_OWN', 'NONE'
] as const;

export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  MANAGE: 'Manage',
  READ: 'Read',
  ASSIGNED_ONLY: 'Assigned only',
  READ_OWN: 'Read own',
  NONE: 'No access'
};

export const ACCESS_LEVEL_HINTS: Record<AccessLevel, string> = {
  MANAGE: 'Read, create, update and delete',
  READ: 'View only',
  ASSIGNED_ONLY: 'Limited to records assigned to the holder',
  READ_OWN: 'Can see only their own records',
  NONE: 'No access'
};

/**
 * Display labels for the modules the matrix is known to carry today. A module missing
 * from this map still renders. `ModuleLabel` falls back to prettifying the raw name,
 * so this list going out of date costs a nicer label, not a broken row.
 */
export const MODULE_LABELS: Record<string, string> = {
  WORKSPACE: 'Workspace',
  ORGANISATION: 'Organisation',
  MEMBERSHIP: 'Membership',
  PLATFORM_USER: 'Platform user',
  ROLE_CATALOGUE: 'Role catalogue',
  ROLE_CONFIG: 'Role configuration',
  DASHBOARD: 'Dashboard',
  DASHBOARD_EXECUTIVE: 'Executive dashboard',
  VULNERABILITY: 'Vulnerability',
  VULN_FIELDS_OPERATIONAL: 'Vulnerability. Operational fields',
  VULN_FIELDS_QUALIFICATION: 'Vulnerability. Qualification fields',
  VULN_ASSIGNMENT: 'Vulnerability assignment',
  COMMENT: 'Comment',
  EVIDENCE: 'Evidence',
  ASSET: 'Asset',
  ASSET_SERVICE: 'Asset service',
  ASSET_ASSIGNMENT: 'Asset assignment',
  APPLICATION: 'Application',
  PROJECT: 'Project',
  TAG: 'Tag',
  REFERENCE_DATA: 'Reference data',
  REPORT: 'Report',
  ACTIVITY_LOG: 'Activity log',
  AUDIT_LOG: 'Audit log',
  CONNECTOR: 'Connector',
  SCAN: 'Scan',
  SLA_CONFIG: 'SLA configuration',
  RISK_CONFIG: 'Risk configuration',
  SMTP_CONFIG: 'SMTP configuration',
  DATA_EXPORT: 'Data export',
  NOTIFICATION: 'Notification',
  LICENCE: 'Licence'
};

/**
 * Words that are acronyms rather than words, and read as nonsense title-cased: the
 * `CISO` role column rendered "Ciso" before this existed. Only the constants this
 * vocabulary actually contains; anything absent just keeps the old behaviour.
 */
const ACRONYMS = new Set([
  'CISO', 'SLA', 'SMTP', 'CVE', 'CWE', 'CPE', 'CVSS', 'EOL', 'ID', 'API', 'URL'
]);

const prettifyWord = (word: string, isFirst: boolean) => {
  if (ACRONYMS.has(word)) return word;
  return isFirst
    ? word.charAt(0) + word.slice(1).toLowerCase()
    : word.toLowerCase();
};

const prettifyConstant = (value: string) => {
  const words = value.split('_').filter(Boolean);
  return words.map((word, i) => prettifyWord(word, i === 0)).join(' ');
};

export const moduleLabel = (module: string): string =>
  MODULE_LABELS[module] ?? prettifyConstant(module);

/**
 * Built-in roles arrive as `SUPERADMIN`, `VULN_ANALYST` and so on. Prettify only those,
 * so anything else keeps the capitalisation it came with.
 */
export const subjectLabel = (subject: string): string =>
  /^[A-Z0-9_]+$/.test(subject) ? prettifyConstant(subject) : subject;

const CUSTOM_SUBJECT_PREFIX = 'custom:';

/**
 * How many custom roles a workspace may hold. The server enforces this. It answers a
 * seventeenth `POST` with 409, so this is only here to disable the add button and say
 * why before the operator fills in a form that cannot succeed.
 */
export const MAX_CUSTOM_ROLES = 16;

/**
 * The built-in role names a custom role may not shadow; the server refuses these with a
 * 400. Listed to fail the form before it is submitted, never to decide what roles exist:
 * the role catalogue and the matrix remain the only source of truth for that.
 */
export const BUILTIN_ROLE_NAMES = [
  'PLATFORM_OWNER', 'SUPERADMIN', 'ADMIN', 'CISO', 'ANALYST', 'DEV', 'VIEWER'
] as const;

const BUILTIN_ROLE_NAME_SET: ReadonlySet<string> = new Set(BUILTIN_ROLE_NAMES);

/** Case-insensitive: "Viewer" shadows `VIEWER` just as surely as the constant does. */
export const shadowsBuiltinRole = (name: string): boolean =>
  BUILTIN_ROLE_NAME_SET.has(name.trim().toUpperCase());

/**
 * A custom role's column in the matrix is keyed `custom:<id>`, **not** by its name.
 * Verified live against the running API, where a role named "Tier 1 Triage" came back as
 * `custom:3`. `PUT /capabilities` in turn refuses `role: "custom:3"` with a 400 ("Name
 * exactly one of 'role' or 'custom_role_id'"), so a column has to be resolved to its id
 * before it can be saved. Returns `null` for a built-in role's column.
 */
export const customRoleIdFromSubject = (subject: string): number | null => {
  if (!subject.startsWith(CUSTOM_SUBJECT_PREFIX)) return null;
  const id = Number(subject.slice(CUSTOM_SUBJECT_PREFIX.length));
  return Number.isInteger(id) ? id : null;
};

/** The matrix key for a custom role, for looking a role up in the other direction. */
export const customSubjectKey = (customRoleId: number): string =>
  `${CUSTOM_SUBJECT_PREFIX}${customRoleId}`;

/* ------------------------------------------------------------------ *
 * Which columns an operator may configure at all
 * ------------------------------------------------------------------ */

/**
 * The built-in tiers the platform ranks. Everything else ranks 0, every custom role a
 * workspace defines included: it is not one of the two named administrative tiers, and
 * where it really sits is the server's answer to give rather than this client's to guess.
 *
 * `userLifecycle.service.ts` gates user administration on the same ranking, and reads it
 * from here so the two cannot drift apart.
 */
export const ROLE_TIER_RANK: Record<string, number> = {
  SUPERADMIN: 2,
  ADMIN: 1
};

/** 0 for a custom role, an unrecognised name, or nothing at all. */
export const roleTierRank = (role: string | null | undefined): number =>
  ROLE_TIER_RANK[(role ?? '').toUpperCase()] ?? 0;

/** The operator looking at the grid, as `GET /capabilities/me` describes them. */
export interface CapabilityViewer {
  role?: string | null;
  customRoleId?: number | null;
}

/**
 * Subjects nobody may ever override, whoever they are, carrying the reason the server
 * gives when one is submitted anyway (`SUBJECT_NOT_CONFIGURABLE`).
 */
const NEVER_CONFIGURABLE: Record<string, string> = {
  SUPERADMIN:
    'Superadmin can never be the subject of an override. The only role above it, Platform owner, holds nothing on the workspace plane, so no ceiling would bound the change.',
  PLATFORM_OWNER:
    'Platform owner lives on the platform plane and is never held as a workspace membership, so it has nothing here to configure.'
};

/**
 * The matrix columns that are the operator's own row. Both keys are taken, because the
 * capability row carries a `role` and a `custom_role_id` field and a workspace that sets
 * both would otherwise leave one of the two columns open.
 */
export const selfSubjectKeys = (viewer: CapabilityViewer | undefined): string[] => {
  if (!viewer) return [];
  const keys: string[] = [];
  if (viewer.role) keys.push(viewer.role);
  if (viewer.customRoleId !== null && viewer.customRoleId !== undefined) {
    keys.push(customSubjectKey(viewer.customRoleId));
  }
  return keys;
};

/**
 * Why this operator cannot configure `subject`'s column, or `null` when they can.
 *
 * Every rule here is the server's, restated so the grid can grey a column out instead of
 * offering a row of dropdowns and a Save that comes back 422 for the whole column:
 *
 * - `SUBJECT_NOT_CONFIGURABLE`: Superadmin and Platform owner, for anyone.
 * - Your own role, which is the case an operator meets first. A role is configured from
 *   above it, and nobody is above themselves.
 * - `TIER_NOT_BELOW`: only roles below your own tier, so an Admin gets the Admin column
 *   greyed out as well as the Superadmin one.
 *
 * A rank of 0 means "not one of the named tiers", not "the bottom of them". An operator
 * holding a custom role with ROLE_CONFIG sits at no rank this client knows, so those
 * columns stay open and the server's refusal is what decides: guessing the other way
 * would hide a legitimate change with no way to discover it.
 */
export const subjectLockReason = (
  subject: string,
  viewer: CapabilityViewer | undefined
): string | null => {
  const never = NEVER_CONFIGURABLE[subject.toUpperCase()];
  if (never) return never;

  // Compared case-blind: an own-role column that failed to match would silently be
  // offered for editing, which is the one case this exists to prevent.
  const own = selfSubjectKeys(viewer).map((key) => key.toUpperCase());
  if (own.includes(subject.toUpperCase())) {
    return 'This is your own role. A role is configured from above it, so nobody widens or narrows their own access here.';
  }

  const viewerRank = roleTierRank(viewer?.role);
  if (viewerRank > 0 && roleTierRank(subject) >= viewerRank) {
    return 'This role sits at or above your own tier. Only the roles below it can be configured here.';
  }

  return null;
};

/**
 * Display labels for the `AuditEvent` values the authority audit returns today. The
 * enum is deliberately small and about *authority*, not about data, but the brief is
 * explicit that new event types will land without a frontend release, so this map is a
 * nicety and never a filter: `auditEventLabel` prettifies anything it does not know.
 */
export const AUDIT_EVENT_LABELS: Record<string, string> = {
  INHERITED_AUTHORITY: 'Inherited authority',
  CAPABILITY_OVERRIDE: 'Capability override',
  CUSTOM_ROLE: 'Custom role',
  SMTP_CONFIG: 'SMTP configuration',
  ORGANISATION_PROVISIONED: 'Organisation provisioned',
  LICENCE_ACTIVATED: 'Licence activated',
  'platform_role.assigned': 'Platform role assigned',
  'platform_role.changed': 'Platform role changed',
  'platform_role.removed': 'Platform role removed',
  'access_invitation.created': 'Invitation sent',
  'access_invitation.resent': 'Invitation resent',
  'access_invitation.revoked': 'Invitation revoked',
  'access_invitation.accepted': 'Invitation accepted',
  'access_invitation.declined': 'Invitation declined',
  'access_invitation.expired': 'Invitation expired',
  'workspace_membership.created': 'Member added',
  'workspace_membership.role_changed': 'Member role changed',
  'workspace_membership.removed': 'Member removed',
  'workspace.created': 'Workspace created'
};

export const auditEventLabel = (event: string): string =>
  AUDIT_EVENT_LABELS[event] ?? prettifyConstant(event);

/**
 * The stable `ViolationCode` values `PUT /capabilities` returns on 422. Unknown codes
 * render their raw value: the endpoint's own `detail` string carries the explanation,
 * so a new code costs nothing.
 */
export const VIOLATION_CODE_LABELS: Record<string, string> = {
  SUBJECT_NOT_CONFIGURABLE: 'Subject not configurable',
  TIER_NOT_BELOW: 'Subject is not below your tier',
  MODULE_IMMUTABLE: 'Module is immutable',
  EXCEEDS_CONFIGURER: 'Exceeds your own access',
  CONFIGURER_NOT_ELIGIBLE: 'You may not configure this subject'
};

export const violationCodeLabel = (code: string): string =>
  VIOLATION_CODE_LABELS[code] ?? code;
